import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { pushDataLayerEvent } from '../utils/analytics';
import './Credits.css';


// Function to calculate one-time price in dollars for given credits
const calculateOneTimePrice = (credits) => {
  if (credits <= 1200) {
    return 50 + (credits - 300) * (100 / 900);
  } else if (credits <= 4000) {
    return 150 + (credits - 1200) * (150 / 2800);
  } else if (credits <= 10000) {
    return 300 + (credits - 4000) * (200 / 6000);
  } else {
    return 500 + (credits - 10000) * 0.03;
  }
};

const getCreditCheckoutEcommerce = ({ itemId, itemName, credits, paymentType, value }) => ({
  value,
  currency: 'USD',
  items: [
    {
      item_id: itemId,
      item_name: itemName,
      item_category: 'Credits',
      price: value,
      quantity: 1,
      credits,
      payment_type: paymentType,
    },
  ],
});

function Credits() {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeFaq, setActiveFaq] = useState(null);
  const [paymentType, setPaymentType] = useState('monthly'); // 'oneTime' or 'monthly'
  const [customCredits, setCustomCredits] = useState(10000);

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const creditPlans = [
    { 
      packageId: "starter",
      name: "Starter Pack",
      credits: 300, 
      oneTimePrice: 50, 
      monthlyPrice: 40,
      bestFor: "Small businesses starting out with address management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    { 
      packageId: "growth",
      name: "Growth Pack",
      credits: 1200, 
      oneTimePrice: 150, 
      monthlyPrice: 120,
      bestFor: "Growing businesses with regular contact management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    { 
      packageId: "scale",
      name: "Scale Pack",
      credits: 4000, 
      oneTimePrice: 300, 
      monthlyPrice: 240,
      bestFor: "Established businesses with high volume needs",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ],
      popular: true
    },
    { 
      packageId: "enterprise",
      name: "Enterprise",
      credits: 10000, 
      oneTimePrice: 500, 
      monthlyPrice: 400,
      bestFor: "Large organizations with specific requirements",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
  ];

  const faqs = [
    {
      question: "What types of postal codes are supported?",
      answer: "ZipCode AutoFill supports global ZIP and postal codes. Enable the experimental International lookup option in your portal configuration, fill in the record's country, then add or change its postal code. Successful international lookups consume 2 credits; successful standard lookups consume 1 credit. Coverage and available fields vary by location, and missing or ambiguous fields remain unchanged."
    },
    {
      question: "Do credits expire?",
      answer: "No, your credits never expire. Use them at your own pace without worrying about timelines."
    },
    {
      question: "When are credits consumed?",
      answer: "Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, no credit is deducted from your account."
    },
    {
      question: "How do monthly subscriptions work?",
      answer: "Monthly subscriptions automatically add credits to your account each month. You can cancel anytime through the Stripe customer portal. Credits from subscriptions never expire, just like one-time purchases."
    }
  ];

  const handlePaymentTypeChange = (type) => {
    console.log('Changing payment type to:', type); // Debug log
    setPaymentType(type);
  };

  const handlePurchase = async (packageId) => {
    console.log('Purchase initiated for:', packageId, 'Payment type:', paymentType); // Debug log
    setError(''); // Clear any previous errors
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-checkout-session`, {
        packageId,
        paymentType
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const selectedPlan = creditPlans.find((plan) => plan.packageId === packageId);
      const value = paymentType === 'monthly' ? selectedPlan.monthlyPrice : selectedPlan.oneTimePrice;

      pushDataLayerEvent({
        event: 'begin_checkout',
        userEmail: user.email,
        checkout_session_id: response.data.sessionId,
        package_id: packageId,
        payment_type: paymentType,
        ecommerce: getCreditCheckoutEcommerce({
          itemId: `credits_${packageId}_${paymentType}`,
          itemName: `${selectedPlan.name} ${paymentType === 'monthly' ? 'Monthly' : 'One-Time'}`,
          credits: selectedPlan.credits,
          paymentType,
          value,
        }),
      });
      
      if (!response.data.url) {
        throw new Error('Stripe Checkout URL was not returned');
      }
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to initiate purchase');
      }
    }
  };

  const handleCustomPurchase = async (credits) => {
    console.log('Custom purchase initiated for:', credits, 'Payment type:', paymentType);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-checkout-session`, {
        customCredits: credits,
        paymentType
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const value = Number((paymentType === 'monthly' ? calculateOneTimePrice(credits) * 0.8 : calculateOneTimePrice(credits)).toFixed(2));

      pushDataLayerEvent({
        event: 'begin_checkout',
        userEmail: user.email,
        checkout_session_id: response.data.sessionId,
        package_id: 'custom',
        payment_type: paymentType,
        ecommerce: getCreditCheckoutEcommerce({
          itemId: `credits_custom_${credits}_${paymentType}`,
          itemName: `${credits.toLocaleString()} Credits ${paymentType === 'monthly' ? 'Monthly' : 'One-Time'}`,
          credits,
          paymentType,
          value,
        }),
      });
      
      if (!response.data.url) {
        throw new Error('Stripe Checkout URL was not returned');
      }
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Custom purchase error:', error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to initiate custom purchase');
      }
    }
  };

  return (
    <div className="credits-container">
      {/* Auto-Top Up Message */}
      <div className="auto-topup-message">
        <div className="auto-topup-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
        <div className="auto-topup-content">
          <h3>Auto-Top Up now available!</h3>
          <p>Use the <a href="/support" className="auto-topup-link">Support Form</a> to set up!</p>
        </div>
      </div>

      <div className="credits-header">
        <div className="credits-shapes">
          <div className="credits-shape shape1"></div>
          <div className="credits-shape shape2"></div>
          <div className="credits-shape shape3"></div>
        </div>
        <h1>Purchase Credits</h1>
        <p className="credits-intro">
          Choose a credit package that suits your needs. The more credits you buy, the more you save!
        </p>
        {message && <p className="success">{message}</p>}
        {error && <p className="error">{error}</p>}
        
        {/* Payment Type Toggle */}
        <div className="payment-type-toggle">
          <button 
            className={`toggle-btn ${paymentType === 'oneTime' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('oneTime')}
          >
            One-Time Purchase
          </button>
          <button 
            className={`toggle-btn ${paymentType === 'monthly' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('monthly')}
          >
            Monthly Subscription
          </button>
        </div>
      </div>

      <div className="credit-plans">
        {creditPlans.map((plan, index) => {
          const currentPrice = paymentType === 'monthly' ? plan.monthlyPrice : plan.oneTimePrice;
          const period = paymentType === 'monthly' ? 'per month' : 'one-time payment';
          const perCredit = paymentType === 'monthly' 
            ? `$${(plan.monthlyPrice / plan.credits).toFixed(3)} per credit/month`
            : `$${(plan.oneTimePrice / plan.credits).toFixed(2)} per credit`;
          
          // Calculate savings for monthly vs one-time per credit
          const oneTimeCostPerCredit = plan.oneTimePrice / plan.credits;
          const monthlyCostPerCredit = plan.monthlyPrice / plan.credits;
          const savingsPercent = Math.round(((oneTimeCostPerCredit - monthlyCostPerCredit) / oneTimeCostPerCredit) * 100);
          
          return (
            <div key={index} className={`credit-plan ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price">${currentPrice}</span>
                <span className="period">{period}</span>
                {paymentType === 'monthly' && savingsPercent > 0 && (
                  <div className="savings-badge">
                    Save {savingsPercent}% vs one-time
                  </div>
                )}
              </div>
              <div className="plan-credits">
                <span className="credits-amount">{plan.credits.toLocaleString()}</span>
                <span className="credits-label">Credits {paymentType === 'monthly' ? 'per month' : ''}</span>
                <span className="per-credit">{perCredit}</span>
              </div>
              <p className="plan-description">{plan.bestFor}</p>
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="plan-action">
                <button onClick={() => handlePurchase(plan.packageId)} className="plan-button">
                  {paymentType === 'monthly' ? 'Subscribe' : 'Purchase'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="custom-slider-container">
        <div className="custom-slider-content">
          <div className="custom-slider-left">
            <h2>Custom Credit Amount</h2>
            <input
              type="range"
              min="10000"
              max="100000"
              step="1000"
              value={customCredits}
              onChange={(e) => setCustomCredits(parseInt(e.target.value))}
            />
            <div className="custom-credits-display">
              <span>{customCredits.toLocaleString()} Credits</span>
              <span>
                ${ (paymentType === 'monthly' ? calculateOneTimePrice(customCredits) * 0.8 : calculateOneTimePrice(customCredits)).toFixed(2) }
                {paymentType === 'monthly' ? ' per month' : ' one-time'}
              </span>
            </div>
          </div>
          <div className="custom-slider-right">
            <button 
              onClick={() => handleCustomPurchase(customCredits)} 
              className="plan-button"
            >
              {paymentType === 'monthly' ? 'Subscribe' : 'Purchase'}
            </button>
          </div>
        </div>
      </div>

      <div className="custom-credits-box">
        <div className="custom-credits-content">
          <div className="custom-credits-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
          </div>
          <div className="custom-credits-text">
            <h2>Need more credits or a custom package?</h2>
            <p>We offer flexible solutions for businesses with unique requirements. Contact us to discuss volume discounts, enterprise licensing, or custom integrations tailored to your specific needs.</p>
          </div>
          <div className="custom-credits-action">
            <a href="mailto:info@zipcodeautofill.com" className="custom-credits-button">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${activeFaq === index ? 'active' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <div className="faq-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12" className="vertical-line"></line>
                  </svg>
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        </div>
        <div className="guarantee-content">
          <h3>Our Guarantee</h3>
          <p>
            Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, 
            no credit is deducted from your account. If you ever have a credit dispute, please reach out to our Support team and we will take care of you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Credits;
